import { defaultTrackingBaseUrl } from '../web/pulse/constants/claims';
import { Claims, Orders } from '../types/database';

import { claimStateMapper } from './claims-orders.helper';

/*
  same functionality in ~/apps/valet/helpers/claims.helper.ts

  TODO: merge in the future
*/

/*
  Claim is considered in the item level if:
  * claimType is `MULTIPLE`
  * claim has multiple values for `claimItem.claimType`
*/

/**
 * Finds the inventory entry for an item by matching against sourceItemId, sourceProductId, or sourceVariantId.
 * Single source of truth for inventory lookup logic across the app.
 */
export function findInventoryForItem<T extends { id?: string }>(
  inventoryList: T[] | undefined,
  item: { sourceItemId?: string | null; sourceProductId?: string | null; sourceVariantId?: string | null } | undefined,
): T | undefined {
  // `item` can be undefined — e.g. an auto-attached personalization line has no matching claim item
  // to resolve inventory against. Guard so the .find below doesn't read sourceItemId off undefined.
  if (!inventoryList?.length || !item) return undefined;

  return inventoryList.find(
    inv => inv.id === item.sourceItemId || inv.id === item.sourceProductId || inv.id === item.sourceVariantId,
  );
}

export function isClaimItemLevel(claim: Claims.Claim) {
  if (String(claim.claimType).toUpperCase() === Claims.ClaimType.MULTIPLE) {
    return true;
  }

  const uniqueClaimTypes = new Set(claim?.claimItems?.map(item => item.claimType));
  return uniqueClaimTypes.size > 1;
}

export function isClaimOrderLevel(claim: Claims.Claim) {
  return !isClaimItemLevel(claim);
}

export const buildTrackingLink = ({
  trackingUrl,
  trackingNumber,
}: {
  trackingUrl?: string;
  trackingNumber?: string;
}) => {
  return trackingUrl ? trackingUrl : defaultTrackingBaseUrl + `/${trackingNumber}`;
};

export const formatClaimType = (claimType?: Claims.ClaimState) => {
  // PR FLAG: can anyone tell me why this was maybe optional?
  // it seems like it should be required and we'd never pass a ClaimState that doesn't exist

  if (!claimType) {
    // I'd like to remove this if we always call this function with a value.
    return '';
  }

  return claimType.replace(/_/g, ' ').toLowerCase();
};

function assertClaimState(claimState: string): asserts claimState is Claims.ClaimState {
  if (!Claims.ClaimState[claimState]) {
    throw Error('not a valid claim state');
  }
}

export const formatClaimState = (claimState: string) => {
  assertClaimState(claimState);

  const state = formatClaimType(claimState);
  return claimStateMapper.label(state);
};

export const ClaimSessionStatusToText: { [key: string]: string } = {
  ISSUES: '/issues',
  PROCESS: '/process',
  ISSUES_ITEMS: '/issues/items',
  ISSUES_SHIPMENT: '/issues/shipment',
  ISSUES_SHIPMENT_DETAILS: '/issues/shipment/details',
  RESOLUTION: '/resolution',
  RESOLUTION_REVIEW: '/resolution/review',
  MULTIPLE_SHIPMENTS: '/multiple-shipments',
};

/**
 * Calculate total claimed quantity for a specific fulfillment item
 *
 * @param claims Array of claims
 * @param sourceItemId Source item ID to match against
 * @returns Total claimed quantity for the item
 */
export function calculateTotalClaimedQuantity(
  claims: Array<{
    claimItems: Array<{
      sourceItemId: string;
      quantity: number;
    }>;
  }>,
  sourceItemId: string,
): number {
  const claimItems = claims
    .flatMap(claim => claim.claimItems)
    .filter(claimItem => claimItem.sourceItemId === sourceItemId);

  return claimItems.reduce((total: number, claimItem) => total + claimItem.quantity, 0);
}

/**
 * Check if a fulfillment item can have more claims filed based on its current claimed quantity
 *
 * @param claims Array of claims
 * @param sourceItemId Source item ID to match against
 * @param fulfillmentQuantity Total quantity in the fulfillment
 * @returns Whether more claims can be filed for this item
 */
export function checkFulfillmentItemCanFileClaim(
  claims: Array<{
    claimItems: Array<{
      sourceItemId: string;
      quantity: number;
    }>;
  }>,
  sourceItemId: string,
  fulfillmentQuantity: number,
): boolean {
  const claimItems = claims
    .flatMap(claim => claim.claimItems)
    .filter(claimItem => claimItem.sourceItemId === sourceItemId);

  const totalClaimedQuantity = claimItems.reduce((total: number, claimItem) => total + claimItem.quantity, 0);

  return totalClaimedQuantity < fulfillmentQuantity;
}

/**
 * Check fulfillment can at least file claim on one item
 *
 * @param fulfillment
 * @param claims
 * @returns
 */
export function checkFulfillmentCanFileClaim(
  fulfillment: {
    items: Array<{
      item: {
        sourceItemId: string;
      };
      quantity: number;
    }>;
  },
  claims: Array<{
    claimItems: Array<{
      sourceItemId: string;
      quantity: number;
    }>;
  }>,
): boolean {
  const canAtLeastClaimOnOneFulfillmentItem = fulfillment.items.some(fulfillmentItem =>
    checkFulfillmentItemCanFileClaim(claims, fulfillmentItem.item.sourceItemId, fulfillmentItem.quantity),
  );

  return canAtLeastClaimOnOneFulfillmentItem;
}

export function isInStoreWarrantyClaim(claim: Claims.Claim) {
  if (!claim.claimCategory || !claim.salesChannel) return false;
  return claim.claimCategory === Claims.ClaimCategory.WARRANTY && claim.salesChannel === Claims.SalesChannel.IN_STORE;
}

/**
 * Extracts reshipment number from resolution details string
 *
 * Looks for a value enclosed in square brackets, e.g., [ORD-12345]
 *
 * @param details - The resolution details string to parse
 * @returns The reshipment number or null if not found
 *
 * @example
 * extractReshipmentNumber("RESHIP [ORD-12345] claim123 @ January 15, 2025")
 * // Returns: "ORD-12345"
 */
export function extractReshipmentNumber(details: string | null | undefined): string | null {
  if (!details || typeof details !== 'string') {
    return null;
  }

  const match = details.match(/\[([^\]\s]+)\]/);

  return match ? match[1] : null;
}

/**
 * Extracts resolved date from resolution details string
 *
 * Supports two formats:
 * 1. Date after @ symbol: "... @ January 15, 2025"
 * 2. Date in brackets on new line: "\n[January 15, 2025] - ..."
 *
 * @param details - The resolution details string to parse
 * @returns The resolved date or null if not found
 *
 * @example
 * extractResolvedDate("RESHIP [ORD-12345] claim123 @ January 15, 2025")
 * // Returns: "January 15, 2025"
 *
 * @example
 * extractResolvedDate("REFUND Resolution claim456\n[January 20, 2025] - Processed externally")
 * // Returns: "January 20, 2025"
 */
export function extractResolvedDate(details: string | null | undefined): string | null {
  if (!details || typeof details !== 'string') {
    return null;
  }

  // Try to match date after @ symbol: "... @ date" (up to newline or end of string)
  const dateMatch = details.match(/@\s*([^\n]+)/);
  if (dateMatch) {
    return dateMatch[1].trim();
  }

  // Try alternative format with date in brackets on new line
  if (details.includes('\n')) {
    const lines = details.split('\n');
    const dateLine = lines.find(line => line.trim().startsWith('['));
    if (dateLine) {
      const altDateMatch = dateLine.match(/\[([^\]]+)\]/);
      if (altDateMatch) {
        return altDateMatch[1];
      }
    }
  }

  return null;
}

/**
 * Generates a source order ID for in-store warranty claims
 * @param options - Configuration options for generating the ID
 * @param options.firstName - Customer first name (required if not using prefix)
 * @param options.lastName - Customer last name (required if not using prefix)
 * @returns Generated source order ID in format: `${initials}-${randomId}`
 */
export function generateInStoreSourceOrderId(options: { firstName: string; lastName: string }): string {
  const randomLetters = () =>
    Math.random()
      .toString(36)
      .replace(/[^a-z]+/g, '')
      .toUpperCase()
      .slice(0, 4);

  const generateId = () => `${randomLetters()}-${randomLetters()}`;

  const initials = `${options.firstName[0]}${options.lastName[0]}`.toUpperCase();
  return `${initials}-${generateId()}`;
}

/**
 * Calculates unfulfilled items from an order by comparing orderItems against fulfilled quantities
 *
 * This function replaces the old OrderModel.unfulfilledItems getter and provides more explicit
 * control over the unfulfilled items calculation.
 *
 * **Important**: This calculation depends on `item.sourceItemId` for matching against fulfilled quantities.
 * - Items are matched using their `sourceItemId` property
 * - The `fulfilledQuantities` Map is keyed by `sourceItemId`
 * - Items with missing or empty `sourceItemId` will have a fallback key of `''` and will not match
 *   real fulfilled items, causing them to be treated as **always unfulfilled**
 *
 * **Edge Case Handling**: Callers should ensure that all items in `orderData.orderItems` have a valid
 * `sourceItemId`, or explicitly handle items with missing IDs as a special case.
 *
 * @param orderData - Partial order data containing orderItems and fulfillments
 * @returns Array of unfulfilled order items with adjusted quantities (remaining quantity clamped to 0 minimum)
 *
 * @example
 * const unfulfilled = getUnfulfilledItems(order);
 * // Returns items where quantity > fulfilled quantity, with remaining amounts
 */
export function getUnfulfilledItems(orderData: Partial<Orders.Order>): Orders.OrderItem[] {
  if (!orderData?.orderItems) return [];

  const fulfillments = orderData.fulfillments || [];

  // Calculate how many of each item have been fulfilled
  const fulfilledQuantities = new Map<string, number>();

  const allFulfillmentItems = fulfillments.flatMap(fulfillment => fulfillment.items || []);

  for (const item of allFulfillmentItems) {
    const rawItem = item.item || item;
    const sourceItemId = rawItem.sourceItemId;

    if (sourceItemId) {
      const currentQty = fulfilledQuantities.get(sourceItemId) || 0;
      fulfilledQuantities.set(sourceItemId, currentQty + (item.quantity || 0));
    }
  }

  // Single pass: compute remaining quantities and filter items with positive remaining quantity
  // This avoids redundant lookups of fulfilledQty compared to the previous filter+map approach
  const result: Orders.OrderItem[] = [];
  for (const item of orderData.orderItems) {
    const fulfilledQty = fulfilledQuantities.get(item.sourceItemId || '') || 0;
    const remainingQty = Math.max(0, item.quantity - fulfilledQty);

    // Only include items with positive remaining quantity
    if (remainingQty > 0) {
      result.push({
        ...item,
        quantity: remainingQty,
      });
    }
  }

  return result;
}

export function filterRecordByResolutionType(
  record: Claims.ClaimResolutionRecord,
  resolutionType: Claims.ResolutionType,
): Claims.ClaimResolutionRecord {
  return {
    ...record,
    lines: record.lines.filter(line => line.resolutionType === resolutionType),
  };
}

export function filterRecordByResolutionTypes(
  record: Claims.ClaimResolutionRecord,
  resolutionTypes: Claims.ResolutionType[],
): Claims.ClaimResolutionRecord {
  return {
    ...record,
    lines: record.lines.filter(line => resolutionTypes.includes(line.resolutionType)),
  };
}

export function isClaimStateFinal(claimState: string): boolean {
  return [
    Claims.ClaimState.APPROVED,
    Claims.ClaimState.DENIED,
    Claims.ClaimState.CLOSED,
    Claims.ClaimState.RESOLVED_BY_BRAND,
  ].includes(claimState as Claims.ClaimState);
}

export interface RecordReshipOrderIdInput {
  clients: { claims: { recordReshipExternalOrderId(recordId: string, reshipExternalOrderId: string): Promise<unknown> } };
  logger: { warn(message: string): void };
  resolutionRecordId: string | null | undefined;
  reshipOrderId: string | number | null | undefined;
}

/**
 * Stamps the created reship order back onto its resolution record, from whichever platform
 * service created it. Never throws — the reship already exists, so a failed stamp must not
 * fail the delivery; the write is idempotent (first writer wins) so a retry is safe.
 */
export async function recordReshipOrderId({
  clients,
  logger,
  resolutionRecordId,
  reshipOrderId,
}: RecordReshipOrderIdInput): Promise<void> {
  if (!resolutionRecordId || reshipOrderId === null || reshipOrderId === undefined || reshipOrderId === '') return;

  try {
    await clients.claims.recordReshipExternalOrderId(resolutionRecordId, String(reshipOrderId));
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logger.warn(`Failed to record reship order ${reshipOrderId} on resolution ${resolutionRecordId}: ${message}`);
  }
}

/**
 * True only once a BullMQ job has burned every attempt. An earlier failure says nothing — the retry
 * may well succeed — so reporting FAILED before this point would accuse a delivery that is still
 * in progress.
 *
 * An UnrecoverableError is the exception: it fails the job outright with attempts left on the clock,
 * so its first failure is its last. Matched by name rather than instance, to keep bullmq out of a
 * library shared with services that have no queues.
 */
export function isFinalAttempt(
  job: { attemptsMade?: number; opts?: { attempts?: number } },
  error?: { name?: string } | null,
): boolean {
  if (error?.name === 'UnrecoverableError') return true;

  return (job.attemptsMade ?? 0) >= (job.opts?.attempts ?? 1);
}
